import { Component, EventEmitter, HostBinding, Input, OnInit, Output, signal } from '@angular/core';
import { faCircleCheck, faCopy, faEnvelope, faKey, faShieldHalved, faSpinnerThird, faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { MultiFactorInfo, TotpSecret } from '@angular/fire/auth';

import { MfaService, TotpReauthenticationChallenge } from '../../services/mfa.service';
import { ToastService } from '../../services/toast.service';
import {
  AuthenticatorAppDownload,
  MfaAuthenticatorAppModalComponent,
} from '../mfa-authenticator-app-modal/mfa-authenticator-app-modal.component';

export type MfaEnrollmentStep = 'verify-email' | 'reauth' | 'second-factor' | 'setup' | 'done';

@Component({
  selector: 'app-mfa-enrollment',
  standalone: false,
  templateUrl: './mfa-enrollment.component.html',
  styleUrls: ['./mfa-enrollment.component.scss'],
})
export class MfaEnrollmentComponent implements OnInit {
  @Input() showCancel = true;
  @Input() cancelLabel = 'Cancel';
  @Input() wideSetup = false;
  @Output() enrolled = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();
  @Output() stepChanged = new EventEmitter<MfaEnrollmentStep>();
  /**
   * Raised when the platform itself cannot enrol a factor — Identity Platform or
   * the TOTP provider is not enabled for the project. Nothing the user does can
   * clear that, so hosts that block on enrolment must offer a way out.
   */
  @Output() unavailable = new EventEmitter<void>();

  faShieldHalved = faShieldHalved;
  faEnvelope = faEnvelope;
  faKey = faKey;
  faCopy = faCopy;
  faCircleCheck = faCircleCheck;
  faSpinnerThird = faSpinnerThird;
  faTriangleExclamation = faTriangleExclamation;

  @HostBinding('class.mfa-enrollment-wide')
  get wideSetupClass(): boolean {
    return this.wideSetup;
  }

  step = signal<MfaEnrollmentStep>('reauth');
  working = signal(false);
  errorMessage = signal<string | null>(null);
  usesPassword = signal(true);
  verificationEmailSent = signal(false);

  password = '';
  reauthCode = '';
  reauthHints: MultiFactorInfo[] = [];
  reauthSelectedHintUid: string | null = null;
  showReauthHintPicker = false;
  code = '';

  private secret: TotpSecret | null = null;
  otpAuthUrl = '';
  secretKey = '';
  qrDataUrl = signal<string | null>(null);
  qrLoading = signal(false);

  readonly authenticatorApps: AuthenticatorAppDownload[] = [
    {
      name: 'Google Authenticator',
      iconUrl: '/assets/img/authenticator-google.jpg',
      iosUrl: 'https://apps.apple.com/app/google-authenticator/id388497605',
      androidUrl: 'https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2',
    },
    {
      name: 'Microsoft Authenticator',
      iconUrl: '/assets/img/authenticator-microsoft.jpg',
      iosUrl: 'https://apps.apple.com/us/app/microsoft-authenticator/id983156458',
      androidUrl: 'https://play.google.com/store/apps/details?id=com.azure.authenticator',
    },
    {
      name: 'Authy',
      iconUrl: '/assets/img/authenticator-authy.jpg',
      iosUrl: 'https://apps.apple.com/us/app/twilio-authy/id494168017',
      androidUrl: 'https://play.google.com/store/apps/details?id=com.authy.authy',
    },
  ];

  constructor(
    private mfaService: MfaService,
    private toastService: ToastService,
    private modalService: NgbModal,
  ) {}

  ngOnInit(): void {
    this.setStep(this.mfaService.isEmailVerified() ? 'reauth' : 'verify-email');
    // Google-only accounts (TRACit staff) have no password to confirm, so they
    // re-authenticate through the provider instead.
    this.usesPassword.set(this.mfaService.usesPasswordCredential());
  }

  private setStep(step: MfaEnrollmentStep): void {
    this.step.set(step);
    this.stepChanged.emit(step);
  }

  private suggestedDeviceName(): string {
    return 'Authenticator app';
  }

  get email(): string {
    return this.mfaService.getCurrentUserEmail() ?? 'your email address';
  }

  get formattedSecretKey(): string {
    return (this.secretKey.match(/.{1,4}/g) ?? [this.secretKey]).join(' ');
  }

  normalizeOtpCode(value: string | number | null | undefined): string {
    return `${value ?? ''}`.replace(/\D/g, '').slice(0, 6);
  }

  formatOtpCode(value: string | number | null | undefined): string {
    const normalized = this.normalizeOtpCode(value);
    return normalized.length > 3
      ? `${normalized.slice(0, 3)} ${normalized.slice(3)}`
      : normalized;
  }

  isOtpComplete(value: string | number | null | undefined): boolean {
    return this.normalizeOtpCode(value).length === 6;
  }

  normalizeOtpInput(event: Event): string {
    const input = event.target as HTMLInputElement | null;
    const normalized = this.normalizeOtpCode(input?.value);
    const formatted = this.formatOtpCode(normalized);
    if (input && input.value !== formatted) {
      input.value = formatted;
    }
    return formatted;
  }

  onReauthCodeInput(event: Event): void {
    this.reauthCode = this.normalizeOtpInput(event);
    if (this.isOtpComplete(this.reauthCode)) {
      void this.submitSecondFactor();
    }
  }

  onCodeInput(event: Event): void {
    this.code = this.normalizeOtpInput(event);
    if (this.isOtpComplete(this.code)) {
      void this.submitCode();
    }
  }

  private async loadQrCode(otpAuthUrl: string): Promise<void> {
    this.qrLoading.set(true);
    this.qrDataUrl.set(null);
    try {
      this.qrDataUrl.set(await this.mfaService.generateQrDataUrl(otpAuthUrl));
    } finally {
      this.qrLoading.set(false);
    }
  }

  private async startEnrollmentSetup(): Promise<void> {
    const start = await this.mfaService.beginTotpEnrollment();
    this.secret = start.secret;
    this.otpAuthUrl = start.qrCodeUrl;
    this.secretKey = start.secretKey;
    this.password = '';
    this.setStep('setup');
    void this.loadQrCode(start.qrCodeUrl);
  }

  private startSecondFactorChallenge(challenge: TotpReauthenticationChallenge): void {
    if (!challenge.hints.length) {
      this.errorMessage.set('No authenticator app is set up on this account. Please contact TRACit support at support@tracit.com.au.');
      return;
    }

    this.password = '';
    this.reauthCode = '';
    this.reauthHints = challenge.hints;
    this.reauthSelectedHintUid = this.reauthHints[0]?.uid ?? null;
    this.showReauthHintPicker = false;
    this.setStep('second-factor');
    this.errorMessage.set(null);
  }

  private clearSecondFactorChallenge(): void {
    this.reauthCode = '';
    this.reauthHints = [];
    this.reauthSelectedHintUid = null;
    this.showReauthHintPicker = false;
  }

  async sendVerificationEmail(): Promise<void> {
    if (this.working()) {
      return;
    }

    this.working.set(true);
    this.errorMessage.set(null);

    try {
      await this.mfaService.sendVerificationEmail();
      this.verificationEmailSent.set(true);
    } catch (error) {
      this.errorMessage.set(this.describeVerificationEmailError(error));
    } finally {
      this.working.set(false);
    }
  }

  async checkEmailVerified(): Promise<void> {
    if (this.working()) {
      return;
    }

    this.working.set(true);
    this.errorMessage.set(null);

    try {
      await this.mfaService.reloadCurrentUser();
      if (this.mfaService.isEmailVerified()) {
        this.setStep('reauth');
        return;
      }
      this.errorMessage.set('We cannot confirm your email yet. Open the confirmation link, then try again.');
    } catch (error) {
      this.errorMessage.set(this.describeVerificationEmailError(error));
    } finally {
      this.working.set(false);
    }
  }

  async submitReauth(): Promise<void> {
    if (this.working() || (this.usesPassword() && !this.password)) {
      return;
    }

    if (!this.mfaService.isEmailVerified()) {
      this.setStep('verify-email');
      this.errorMessage.set('Confirm your email address before setting up two-factor authentication.');
      return;
    }

    this.working.set(true);
    this.errorMessage.set(null);

    try {
      const challenge = this.usesPassword()
        ? await this.mfaService.reauthenticateWithPassword(this.password)
        : await this.mfaService.reauthenticateWithGoogle();

      if (challenge) {
        this.startSecondFactorChallenge(challenge);
        return;
      }
    } catch (error) {
      this.errorMessage.set(this.describeReauthError(error));
      return;
    } finally {
      this.working.set(false);
    }

    this.working.set(true);
    try {
      await this.startEnrollmentSetup();
    } catch (error) {
      this.errorMessage.set(this.describeEnrollmentStartError(error));
    } finally {
      this.working.set(false);
    }
  }

  async submitSecondFactor(): Promise<void> {
    const reauthCode = this.normalizeOtpCode(this.reauthCode);
    this.reauthCode = this.formatOtpCode(reauthCode);
    if (this.working() || !this.reauthSelectedHintUid || reauthCode.length < 6) {
      return;
    }

    this.working.set(true);
    this.errorMessage.set(null);

    try {
      await this.mfaService.resolveTotpReauthentication(this.reauthSelectedHintUid, reauthCode);
      this.clearSecondFactorChallenge();
    } catch (error) {
      this.errorMessage.set(this.describeSecondFactorError(error));
      this.working.set(false);
      return;
    }

    try {
      await this.startEnrollmentSetup();
    } catch (error) {
      this.errorMessage.set(this.describeEnrollmentStartError(error));
    } finally {
      this.working.set(false);
    }
  }

  selectReauthHint(uid: string): void {
    this.reauthSelectedHintUid = uid;
    this.errorMessage.set(null);
    this.reauthCode = '';
  }

  get selectedReauthHintLabel(): string {
    const hint = this.reauthHints.find((candidate) => candidate.uid === this.reauthSelectedHintUid);
    return hint?.displayName || 'your authenticator app';
  }

  async submitCode(): Promise<void> {
    const code = this.normalizeOtpCode(this.code);
    this.code = this.formatOtpCode(code);
    if (this.working() || !this.secret || code.length < 6) {
      return;
    }

    this.working.set(true);
    this.errorMessage.set(null);

    try {
      await this.mfaService.completeTotpEnrollment(this.secret, code, this.suggestedDeviceName());
      this.setStep('done');
      this.enrolled.emit();
    } catch (error) {
      this.errorMessage.set(this.describeCodeError(error));
    } finally {
      this.working.set(false);
    }
  }

  copySecretKey(): void {
    if (!this.secretKey) {
      return;
    }
    void navigator.clipboard.writeText(this.secretKey).then(() => {
      this.toastService.show('Setup key copied to clipboard.', {
        classname: 'bg-primary-subtle text-dark',
        header: 'Copied',
        delay: 2500,
      });
    });
  }

  openAuthenticatorApp(app: AuthenticatorAppDownload): void {
    const ref = this.modalService.open(MfaAuthenticatorAppModalComponent, {
      centered: true,
      size: 'sm',
    });
    ref.componentInstance.app = app;
  }

  cancel(): void {
    this.cancelled.emit();
  }

  private describeReauthError(error: unknown): string {
    const code = (error as { code?: string })?.code;
    switch (code) {
      case 'auth/wrong-password':
      case 'auth/invalid-credential':
        return this.usesPassword()
          ? 'That password is not correct. Please try again.'
          : 'We could not confirm your Google account. Please try again.';
      case 'auth/too-many-requests':
        return 'Too many attempts. Please wait a moment and try again.';
      case 'auth/popup-closed-by-user':
      case 'auth/cancelled-popup-request':
        return 'The Google sign-in window was closed before you finished. Please try again.';
      case 'auth/popup-blocked':
        return 'Your browser blocked the Google sign-in window. Allow pop-ups for this site and try again.';
      case 'auth/user-mismatch':
        return 'That Google account does not match the account you are signed in as.';
      default:
        return this.usesPassword()
          ? 'We could not verify your password. Please try again.'
          : 'We could not confirm your Google account. Please try again.';
    }
  }

  private describeVerificationEmailError(error: unknown): string {
    const code = (error as { code?: string })?.code;
    switch (code) {
      case 'auth/too-many-requests':
        return 'Too many attempts. Please wait a moment and try again.';
      case 'auth/requires-recent-login':
        this.setStep('reauth');
        return this.usesPassword()
          ? 'For security, please confirm your password before sending another confirmation email.'
          : 'For security, please confirm your Google account before sending another confirmation email.';
      default:
        return 'We could not send the confirmation email right now. Please try again.';
    }
  }

  private describeEnrollmentStartError(error: unknown): string {
    const code = (error as { code?: string })?.code;
    const message = `${(error as { message?: string })?.message ?? ''}`.toLowerCase();

    if (code === 'auth/unverified-email' || message.includes('email') && message.includes('verified')) {
      this.setStep('verify-email');
      return 'Confirm your email address before setting up two-factor authentication.';
    }

    switch (code) {
      case 'auth/operation-not-allowed':
        this.unavailable.emit();
        return 'Two-factor authentication is not enabled for this TRACit environment yet.';
      case 'auth/requires-recent-login':
      case 'auth/invalid-multi-factor-session':
      case 'auth/missing-multi-factor-session':
        this.setStep('reauth');
        return this.usesPassword()
          ? 'For security, please confirm your password again.'
          : 'For security, please confirm your Google account again.';
      case 'auth/maximum-second-factor-count-exceeded':
        return 'This account already has an authenticator set up. Remove it before setting up a new one.';
      case 'auth/unsupported-first-factor':
        return 'This sign-in method cannot add two-factor authentication. Sign out, sign in with your password, then try again.';
      default:
        console.error('[MfaEnrollmentComponent] Failed to start MFA enrollment', error);
        return 'We could not start two-factor setup right now. Please try again.';
    }
  }

  private describeSecondFactorError(error: unknown): string {
    const code = (error as { code?: string })?.code;
    const invalidCode = code === 'auth/invalid-verification-code' || code === 'auth/invalid-payload';

    if (invalidCode && this.reauthHints.length > 1) {
      this.showReauthHintPicker = true;
      return `That code is not valid. Check you're reading ${this.selectedReauthHintLabel}, or choose a different device.`;
    }

    switch (code) {
      case 'auth/invalid-verification-code':
      case 'auth/invalid-payload':
        return 'That code is not valid. Check your authenticator app and try again.';
      case 'auth/too-many-requests':
        return 'Too many attempts. Please wait a moment and try again.';
      case 'auth/requires-recent-login':
        this.setStep('reauth');
        return this.usesPassword()
          ? 'For security, please confirm your password again.'
          : 'For security, please confirm your Google account again.';
      default:
        return 'We could not verify that code. Please try again.';
    }
  }

  private describeCodeError(error: unknown): string {
    const code = (error as { code?: string })?.code;
    switch (code) {
      case 'auth/invalid-verification-code':
      case 'auth/invalid-payload':
        return 'That code is not valid. Check your authenticator app and try again.';
      case 'auth/requires-recent-login':
        this.setStep('reauth');
        return this.usesPassword()
          ? 'For security, please confirm your password again.'
          : 'For security, please confirm your Google account again.';
      case 'auth/second-factor-already-in-use':
        return 'This authenticator is already set up on your account.';
      case 'auth/operation-not-allowed':
        this.unavailable.emit();
        return 'Two-factor authentication is not enabled for this TRACit environment yet.';
      default:
        return 'We could not verify that code. Please try again.';
    }
  }
}
